import React from 'react';
import { FORMULA } from '../../mock';
import { Brain, Heart, Footprints, IndianRupee, ChevronDown } from 'lucide-react';

const TONES = {
  violet: {
    grad: 'from-violet-500/15 to-fuchsia-500/5',
    border: 'border-violet-400/25',
    text: 'text-violet-300',
    chip: 'bg-violet-500/10 text-violet-300 border-violet-400/30',
    glow: 'bg-violet-500/15',
  },
  sky: {
    grad: 'from-sky-500/15 to-blue-500/5',
    border: 'border-sky-400/25',
    text: 'text-sky-300',
    chip: 'bg-sky-500/10 text-sky-300 border-sky-400/30',
    glow: 'bg-sky-500/15',
  },
  emerald: {
    grad: 'from-emerald-500/15 to-lime-500/5',
    border: 'border-emerald-400/25',
    text: 'text-emerald-300',
    chip: 'bg-emerald-500/10 text-emerald-300 border-emerald-400/30',
    glow: 'bg-emerald-500/15',
  },
};

export default function Formula() {
  return (
    <section id="formula" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center">
          <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5">
            The Signature Method
          </p>
          <h2 className="font-serif text-white text-[36px] md:text-[58px] leading-[1.05] tracking-tight whitespace-pre-line">
            {FORMULA.heading}
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-neutral-400 text-[15px] md:text-[17px] leading-relaxed">
            {FORMULA.sub}
          </p>
        </div>

        <div className="mt-14 space-y-6">
          {FORMULA.steps.map((s, i) => {
            const t = TONES[s.tone];
            return (
              <React.Fragment key={s.n}>
                <div
                  className={`relative overflow-hidden rounded-3xl border ${t.border} bg-gradient-to-br ${t.grad} backdrop-blur-sm`}
                >
                  <div className={`absolute -top-24 -right-24 w-72 h-72 rounded-full ${t.glow} blur-3xl`} />
                  <div className="relative grid md:grid-cols-[160px_1fr] gap-6 md:gap-8 p-6 md:p-9">
                    <div className="flex md:flex-col items-center md:items-start gap-4 md:gap-3">
                      <div className={`font-serif text-white text-[72px] md:text-[96px] leading-none`}>
                        {s.n}
                      </div>
                      <span className={`inline-flex items-center text-[11px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${t.chip}`}>
                        {s.days}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-serif text-white text-[28px] md:text-[36px] tracking-tight">
                        {s.title}
                      </h3>
                      <p className={`mt-1 text-[13px] md:text-[14px] ${t.text}`}>
                        {s.sub}
                      </p>
                      <div className="mt-6 grid sm:grid-cols-3 gap-4">
                        {s.bullets.map((b) => (
                          <div
                            key={b.t}
                            className="rounded-xl border border-white/10 bg-navy-950/40 p-4"
                          >
                            <p className={`text-[13px] font-semibold ${t.text}`}>
                              {b.t}
                            </p>
                            <p className="mt-1 text-neutral-300 text-[13px] leading-relaxed">
                              {b.d}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 flex items-start gap-3 rounded-xl border border-white/10 bg-navy-950/40 p-4">
                        <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded border ${t.chip}`}>
                          Outcome
                        </span>
                        <p className="text-neutral-200 text-[14px] leading-relaxed">
                          {s.outcome}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {i < FORMULA.steps.length - 1 && (
                  <div className="flex justify-center">
                    <ChevronDown className="text-amber-300/80" size={32} strokeWidth={1.5} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-7 items-center gap-3 md:gap-2 text-center">
          <EqItem icon={Brain} label={FORMULA.equation[0]} colorClass="text-violet-300" />
          <Plus />
          <EqItem icon={Heart} label={FORMULA.equation[1]} colorClass="text-sky-300" />
          <Plus />
          <EqItem icon={Footprints} label={FORMULA.equation[2]} colorClass="text-emerald-300" />
          <Equals />
          <EqItem icon={IndianRupee} label={FORMULA.equation[3]} colorClass="text-amber-300" />
        </div>

        <p className="mt-10 text-center font-serif text-amber-200/90 italic text-[16px] md:text-[18px]">
          You don't need more time. You need a new formula.
        </p>
      </div>
    </section>
  );
}

function EqItem({ icon: Icon, label, colorClass }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl border border-white/10 bg-navy-950 flex items-center justify-center ${colorClass}`}>
        <Icon size={26} strokeWidth={1.5} />
      </div>
      <p className="text-[11px] md:text-[12px] tracking-[0.22em] uppercase text-neutral-300">
        {label}
      </p>
    </div>
  );
}

function Plus() {
  return <div className="text-amber-300/80 text-2xl font-light hidden md:flex justify-center">+</div>;
}
function Equals() {
  return <div className="text-amber-300/80 text-2xl font-light hidden md:flex justify-center">=</div>;
}
